import React from "react";
import PropTypes from "prop-types";
import { Modal, Button, Form } from "react-bootstrap";
import "../../assets/css/AttachmentModal.css";

const AttachmentModal = ({
  show,
  onHide,
  attachmentUrl,  
  acknowledge,
  setAcknowledge,
  currentStatus,
  reimbursementId,
  reimbursementStatusIdHandler,
  config,
}) => {
  const statusOptions = [
    { value: 1, label: "Pending", className: "text-info" },
    { value: 2, label: "Approved", className: "text-success" },
    { value: 3, label: "Rejected", className: "text-danger" },
  ];

  const getStatusInfo = () => {
    return (
      statusOptions.find((option) => option.value === currentStatus) ||
      statusOptions[0]
    );
  };

  const handleApproveReject = async (status) => {
    try {
      await reimbursementStatusIdHandler(status, reimbursementId, "REIMBURSEMENT");
      // reset acknowledge checkbox and close modal on success
      setAcknowledge(false);
      onHide();
    } catch (error) {
      // handler shows toast on error — keep modal open so user can retry
      console.error("Status update failed:", error);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="attachment-modal-title"
      className="attachment-modal"
      centered
    >
      <Modal.Header className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <Modal.Title id="attachment-modal-title">Uploaded Attachment</Modal.Title>

          <span
            style={{
              padding: "4px 10px",
              borderRadius: "14px",
              fontSize: "0.85rem",
              fontWeight: 600,
              color: currentStatus === 1 ? "#333" : "#fff",
              backgroundColor:
                currentStatus === 1 ? "#ffdf00" : currentStatus === 2 ? "#28a745" : "#dc3545",
            }}
            title={`Current status: ${getStatusInfo().label}`}>
            {getStatusInfo().label}
          </span>
        </div>

        <Button
          variant="link"
          onClick={() => {
            setAcknowledge(false);
            onHide();
          }}
          className="modal-close-btn"
        >
          <i className="fa-solid fa-rectangle-xmark" />
        </Button>
      </Modal.Header>

      <Modal.Body className="d-flex justify-content-center">
        {attachmentUrl ? (
          (() => {
            const fullUrl = `${config.IMAGE_URL}${attachmentUrl}`;
            const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(attachmentUrl);
            const isPdf = /\.pdf$/i.test(attachmentUrl);

            if (isImage) {
              return (
                <img
                  src={fullUrl}
                  alt="Attachment"
                  className="attachment-image"
                  style={{ maxWidth: "100%", maxHeight: "500px", objectFit: "contain" }}
                />
              );
            }
            if (isPdf) {
              return (
                 <iframe
                      src={`${fullUrl}#toolbar=0`}
                      title="PDF Attachment"
                      style={{
                        width: "100%",
                        height: "500px",
                        border: "none",
                      }}
                    />
              );
            }
            return (
              <div className="d-flex flex-column align-items-center">
                <i className="fa fa-file" style={{ fontSize: "80px", color: "#999", marginBottom: "20px" }} />
                <p className="mb-3">Preview not available for this file type</p>
                <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  <i className="fa fa-download me-2" />
                  Download Attachment
                </a>
              </div>
            );
          })()
        ) : (
          <div className="text-center">
            <p className="text-muted">No attachment available</p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="w-100 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <Form.Check
              id={`ack-${reimbursementId}`}
              type="checkbox"
              checked={acknowledge || currentStatus !== 1}
              onChange={(e) => setAcknowledge(e.target.checked)}
              disabled={currentStatus !== 1}
              label={<span style={{ fontWeight: 600 }}>I have reviewed and acknowledge this attachment.</span>}
            />
          </div>

          <div className="d-flex align-items-center gap-2">

            {currentStatus === 1 ? (
              acknowledge ? (
                <>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={() => handleApproveReject(2)}
                  >
                    Approve
                  </button>

                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => handleApproveReject(3)}
                  >
                    Reject
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-outline-secondary btn-sm" disabled>
                  No action
                </button>
              )
            ) : null}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

AttachmentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  attachmentUrl: PropTypes.string.isRequired,
  acknowledge: PropTypes.bool.isRequired,
  setAcknowledge: PropTypes.func.isRequired,
  currentStatus: PropTypes.number.isRequired,
  reimbursementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  reimbursementStatusIdHandler: PropTypes.func.isRequired,
  config: PropTypes.shape({
    IMAGE_URL: PropTypes.string.isRequired,
  }).isRequired,
};

export default AttachmentModal;
